import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LinkIcon,
  Plus,
  ExternalLink,
  Trash2,
  BarChart3,
  Eye,
  EyeOff,
  Save,
  Copy,
  Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

const publicFunnelUrl = (slug) => {
  if (!slug) return "";
  return `${window.location.origin}/f/${slug}`;
};

export default function FunnelPanel({ lang, onSlugChange }) {
  const t = useT(lang);

  // Profile
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Links
  const [links, setLinks] = useState([]);
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkIcon, setLinkIcon] = useState("");
  const [creatingLink, setCreatingLink] = useState(false);

  // Analytics
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    try {
      const [{ data: p }, { data: l }, { data: a }] = await Promise.all([
        api.get("/funnel-profile"),
        api.get("/funnel-links"),
        api.get("/analytics/funnel"),
      ]);
      if (p && !p._empty && !p._needs_migration) {
        setSlug(p.slug || "");
        setDisplayName(p.display_name || "");
        setBio(p.bio || "");
        setAvatarUrl(p.avatar_url || "");
        onSlugChange?.(p.slug || null);
      }
      setLinks(Array.isArray(l) ? l : []);
      setAnalytics(a || null);
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 401) {
        toast.error(e?.response?.data?.detail || String(e));
      }
    } finally {
      setProfileLoaded(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProfile = async () => {
    const cleanSlug = (slug || "").trim().toLowerCase();
    if (!cleanSlug) {
      toast.error(t("funnelSlugRequired"));
      return;
    }
    setSavingProfile(true);
    try {
      const { data } = await api.put("/funnel-profile", {
        slug: cleanSlug,
        display_name: displayName,
        bio,
        avatar_url: avatarUrl || null,
      });
      setSlug(data.slug);
      onSlugChange?.(data.slug);
      toast.success(t("funnelProfileSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      setSavingProfile(false);
    }
  };

  const addLink = async () => {
    if (!linkTitle.trim() || !linkUrl.trim()) {
      toast.error(t("funnelLinkRequired"));
      return;
    }
    setCreatingLink(true);
    try {
      const { data } = await api.post("/funnel-links", {
        title: linkTitle.trim(),
        destination_url: linkUrl.trim(),
        icon: linkIcon.trim(),
        sort_order: links.length,
        is_active: true,
      });
      setLinks((prev) => [...prev, data]);
      setLinkTitle("");
      setLinkUrl("");
      setLinkIcon("");
      // refresh analytics per-link list
      const { data: a } = await api.get("/analytics/funnel");
      setAnalytics(a);
      toast.success(t("funnelLinkCreated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      setCreatingLink(false);
    }
  };

  const toggleActive = async (link) => {
    try {
      const { data } = await api.patch(`/funnel-links/${link.id}`, {
        is_active: !link.is_active,
      });
      setLinks((prev) => prev.map((l) => (l.id === link.id ? data : l)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    }
  };

  const removeLink = async (id) => {
    try {
      await api.delete(`/funnel-links/${id}`);
      setLinks((prev) => prev.filter((l) => l.id !== id));
      const { data: a } = await api.get("/analytics/funnel");
      setAnalytics(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    }
  };

  const copyPublicUrl = async () => {
    const url = publicFunnelUrl(slug);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      toast.success(t("funnelUrlCopied"));
      setTimeout(() => setCopiedUrl(false), 1500);
    } catch {
      toast.error(t("errorGeneric"));
    }
  };

  const perLinkStats = analytics?.perLink || [];
  const statFor = (linkId) => perLinkStats.find((r) => r.link_id === linkId);

  if (!profileLoaded) {
    return (
      <div className="surface p-6 md:p-7" data-testid="funnel-panel-loading">
        <span className="typing"><span /><span /><span /></span>
      </div>
    );
  }

  return (
    <div className="surface p-6 md:p-7" data-testid="funnel-panel">
      <div className="flex items-center gap-2 mb-1">
        <LinkIcon size={14} strokeWidth={1.5} className="text-[#FF006E]" />
        <span className="overline">{t("funnelOverline")}</span>
      </div>
      <h2 className="font-display text-lg font-semibold mb-5">
        {t("funnelTitle")}
      </h2>

      {/* Public URL preview */}
      {slug && (
        <div
          className="surface-inner p-4 mb-5 flex items-center justify-between gap-3"
          data-testid="funnel-public-url-block"
        >
          <div className="min-w-0">
            <span className="overline block mb-1">{t("funnelPublicUrl")}</span>
            <a
              href={publicFunnelUrl(slug)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FF006E] hover:text-[#ff338b] text-sm font-mono truncate block"
              data-testid="funnel-public-url"
            >
              {publicFunnelUrl(slug)}
            </a>
          </div>
          <button
            type="button"
            onClick={copyPublicUrl}
            className="text-zinc-300 hover:text-white transition-colors inline-flex items-center gap-1.5 text-xs uppercase tracking-widest"
            data-testid="copy-public-url-btn"
          >
            {copiedUrl ? (
              <Check size={14} strokeWidth={1.8} />
            ) : (
              <Copy size={14} strokeWidth={1.6} />
            )}
            {copiedUrl ? t("copied") : t("copyBtn")}
          </button>
        </div>
      )}

      {/* Profile form */}
      <div className="space-y-4">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">
            {t("funnelSlug")}
          </Label>
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="ella"
            className="field-input bg-transparent font-mono"
            data-testid="funnel-slug-input"
          />
          <p className="text-[11px] text-zinc-500 mt-1">{t("funnelSlugHint")}</p>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">
            {t("funnelDisplayName")}
          </Label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Ella"
            className="field-input bg-transparent"
            data-testid="funnel-display-name-input"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">
            {t("funnelBio")}
          </Label>
          <Textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder={t("funnelBioPh")}
            className="field-textarea bg-transparent min-h-[70px]"
            data-testid="funnel-bio-input"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">
            {t("funnelAvatar")}
          </Label>
          <Input
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            placeholder="https://.../avatar.jpg"
            className="field-input bg-transparent"
            data-testid="funnel-avatar-input"
          />
        </div>

        <button
          type="button"
          onClick={saveProfile}
          disabled={savingProfile}
          className="cta-primary rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] inline-flex items-center gap-2"
          data-testid="save-funnel-profile-btn"
        >
          <Save size={14} strokeWidth={1.7} />
          {savingProfile ? t("saving") : t("funnelSaveProfile")}
        </button>
      </div>

      <Separator className="bg-white/5 my-6" />

      {/* Analytics summary */}
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={14} strokeWidth={1.5} className="text-[#FF006E]" />
        <span className="overline">{t("funnelAnalytics")}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="funnel-analytics">
        <Stat label={t("funnelTotal")} value={analytics?.total ?? 0} testid="stat-total" />
        <Stat label={t("funnelLast7")} value={analytics?.last7 ?? 0} testid="stat-7d" />
        <Stat label={t("funnelLast30")} value={analytics?.last30 ?? 0} testid="stat-30d" />
      </div>

      {/* Links CRUD */}
      <div className="flex items-center gap-2 mb-3">
        <LinkIcon size={14} strokeWidth={1.5} className="text-[#FF006E]" />
        <span className="overline">{t("funnelLinks")}</span>
      </div>

      <div className="surface-inner p-4 mb-5">
        <div className="grid grid-cols-[60px_1fr] gap-3 mb-3">
          <Input
            value={linkIcon}
            onChange={(e) => setLinkIcon(e.target.value)}
            placeholder="🔗"
            maxLength={4}
            className="field-input bg-transparent text-center text-lg"
            data-testid="new-link-icon-input"
          />
          <Input
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.target.value)}
            placeholder={t("funnelLinkTitlePh")}
            className="field-input bg-transparent"
            data-testid="new-link-title-input"
          />
        </div>
        <Input
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="https://fanvue.com/..."
          className="field-input bg-transparent mb-3"
          data-testid="new-link-url-input"
        />
        <button
          type="button"
          onClick={addLink}
          disabled={creatingLink}
          className="cta-primary rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] inline-flex items-center gap-2"
          data-testid="create-link-btn"
        >
          <Plus size={14} strokeWidth={1.8} />
          {creatingLink ? t("saving") : t("funnelAddLink")}
        </button>
      </div>

      <div className="space-y-3" data-testid="funnel-links-list">
        {links.length === 0 ? (
          <p className="text-zinc-500 text-sm" data-testid="funnel-links-empty">
            {t("funnelLinksEmpty")}
          </p>
        ) : (
          links.map((link) => {
            const s = statFor(link.id);
            return (
              <div
                key={link.id}
                className="surface-inner p-4 flex items-center justify-between gap-3"
                data-testid={`link-row-${link.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{link.icon || "🔗"}</span>
                    <p className="font-medium text-sm truncate">{link.title}</p>
                    {!link.is_active && (
                      <span className="text-[9px] uppercase tracking-widest border border-zinc-700 text-zinc-500 rounded px-1.5 py-[1px]">
                        {t("inactive")}
                      </span>
                    )}
                  </div>
                  <a
                    href={link.destination_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-zinc-500 hover:text-zinc-300 truncate block mt-0.5 inline-flex items-center gap-1"
                  >
                    <ExternalLink size={10} strokeWidth={1.6} />
                    {link.destination_url}
                  </a>
                  <p
                    className="text-[10px] text-[#FF006E] uppercase tracking-widest mt-1"
                    data-testid={`link-clicks-${link.id}`}
                  >
                    {s?.clicks ?? 0} {t("funnelClicksLabel")}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleActive(link)}
                    className="text-zinc-500 hover:text-white transition-colors"
                    title={link.is_active ? t("hide") : t("show")}
                    data-testid={`toggle-active-${link.id}`}
                  >
                    {link.is_active ? (
                      <Eye size={15} strokeWidth={1.6} />
                    ) : (
                      <EyeOff size={15} strokeWidth={1.6} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeLink(link.id)}
                    className="text-zinc-500 hover:text-[#FF006E] transition-colors"
                    data-testid={`delete-link-${link.id}`}
                  >
                    <Trash2 size={15} strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="surface-inner p-3 text-center" data-testid={testid}>
      <p className="font-display text-2xl font-semibold text-white">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
        {label}
      </p>
    </div>
  );
}
